import { wrapRequest } from '@applandeo/api-wrapper';
import { areIntervalsOverlapping, isValid } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ALERT_VARIANT, useAlertMessages } from '@/components/Global/AlertMessages/AlertMessagesProvider';
import { getAbsenceTypes } from '@/dataExchange/services/myOrganization.service';
import { getAllUserRequests } from '@/dataExchange/services/newRequest.service';
import { AbsenceTypeResponseDto } from '@/domain/myOrganization';
import { RequestWithDates } from '@/domain/newRequest';
import { DatePickerRange } from '@/domain/requestsPage';
import { COLOR } from '@/globalStyles';
import { useSentryActions } from '@/sentry/hooks/useSentryActions';
import { SENTRY_ACTIONS } from '@/sentry/sentryActions';
import { DATE_FORMAT, formatAbsenceISODatesToDates } from '@/utils/date/date';
import { DELETION_STATUS, REQUEST_STATUS } from '@/utils/enums/requestsPage';

const useCalendarLogic = ({
    dateRange,
    reloadDataOnReset,
    userId,
    showDesktopVersion = true,
}: {
    dateRange: DatePickerRange;
    reloadDataOnReset?: unknown[];
    userId?: string;
    showDesktopVersion?: boolean;
}) => {
    const getAbsenceTypesRequest = wrapRequest(getAbsenceTypes);
    const getUserAllRequestsWrapper = wrapRequest(getAllUserRequests);
    const { t } = useTranslation();
    const { createAlert } = useAlertMessages();
    const { handleSentryAction } = useSentryActions();

    const [allUserAbsences, setAllUserAbsences] = useState<RequestWithDates[]>();
    const [absenceTypes, setAbsenceTypes] = useState<AbsenceTypeResponseDto[]>();

    const getAbsenceTypesData = () => {
        getAbsenceTypesRequest.fetch(true).onSuccess(response => {
            setAbsenceTypes(response);
        });
    };

    const getUserAllRequests = useCallback(() => {
        if (!userId) return;

        getUserAllRequestsWrapper
            .fetch(userId)
            .onAnyError(error => {
                createAlert(ALERT_VARIANT.error, t('newRequest_alerts_failed_to_get_all_user_requests'));
                handleSentryAction(error, SENTRY_ACTIONS.ERROR_GET_USER_REQUESTS);
            })
            .onSuccess(response => {
                const absencesWithDates = formatAbsenceISODatesToDates(response);
                setAllUserAbsences(absencesWithDates);
            });
    }, [createAlert, getUserAllRequestsWrapper, t, userId]);

    useEffect(getAbsenceTypesData, []);
    useEffect(getUserAllRequests, [userId, reloadDataOnReset]);

    const isRangesOverlapping = useMemo(() => {
        if (!isValid(dateRange.startDate) || !isValid(dateRange.endDate) || !allUserAbsences) return;

        return allUserAbsences?.some(absence => {
            if (
                absence?.status === DELETION_STATUS.DELETION_REQUEST_ACCEPTED ||
                absence?.status === REQUEST_STATUS.REJECTED
            ) {
                return false;
            }

            return areIntervalsOverlapping(
                { start: dateRange.startDate || 0, end: dateRange.endDate ?? 0 },
                { start: absence.from, end: absence.to },
                { inclusive: true }
            );
        });
    }, [allUserAbsences, dateRange.endDate, dateRange.startDate]);

    const allRequestsWithColors = useMemo(() => {
        const filteredAbsences = allUserAbsences?.filter(
            absence =>
                absence?.status !== DELETION_STATUS.DELETION_REQUEST_ACCEPTED &&
                absence?.status !== REQUEST_STATUS.REJECTED
        );

        return filteredAbsences?.map(absence => {
            const absenceTypeData = absenceTypes?.find(absenceType => absenceType?._id === absence?.absenceTypeId);

            if (absenceTypeData) {
                return {
                    ...absence,
                    color: absenceTypeData?.color,
                    isCustomType: absenceTypeData.predefinedType ? false : true,
                    isHiddenType: false,
                };
            }

            const color = absence.isWorking ? COLOR.pastelMint : COLOR.coolMint;

            return { ...absence, color, isCustomType: false, isHiddenType: true };
        });
    }, [absenceTypes, allUserAbsences]);

    const calendarConfig = useMemo(
        () => ({
            startDatePlaceholder: DATE_FORMAT.FULL_WITH_DOTS,
            endDatePlaceholder: DATE_FORMAT.FULL_WITH_DOTS,
            months: showDesktopVersion ? 2 : 1,
        }),
        [showDesktopVersion]
    );

    return {
        calendarConfig,
        allRequestsWithColors,
        isRangesOverlapping,
        absenceTypes,
    };
};

export default useCalendarLogic;
